import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import socket from "../../socket";
import { pathVideoCall } from "../../config/constants";
import { Popup, CallerText, ActionRow, AnswerBtn, RejectBtn } from "./styles";
import ringToneSrc from "../../assets/ring_tone.wav";

const CallNotification = () => {
  const { t } = useTranslation();
  const [callData, setCallData] = useState(null);   // llamada entrante
  const [waiting, setWaiting] = useState(null);     // paciente esperando en sala
  const locationRef = useRef(null);
  const ringToneRef = useRef(null);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => { locationRef.current = location; }, [location]);

  useEffect(() => {
    const ring = new Audio(ringToneSrc);
    ring.loop = true;
    ringToneRef.current = ring;
    return () => { ring.pause(); ring.currentTime = 0; };
  }, []);

  useEffect(() => {
    const ring = ringToneRef.current;
    if (!ring) return;
    if (callData) {
      ring.currentTime = 0;
      ring.play().catch(() => {});
    } else {
      ring.pause();
      ring.currentTime = 0;
    }
  }, [callData]);

  useEffect(() => {
    const authenticate = () => {
      const token = localStorage.getItem("token");
      if (token) socket.emit("authenticate", token);
    };
    if (socket.connected) authenticate();
    socket.on("me", authenticate);

    const onVideoCallScreen = () => locationRef.current?.pathname === pathVideoCall;

    const handleCall = (data) => {
      // Si el usuario ya está en la videollamada, VideoCallComponent lo gestiona
      if (onVideoCallScreen()) return;
      if (!localStorage.getItem("token")) return;
      setCallData(data);
    };

    // El paciente ha entrado en la sala y espera: el terapeuta decide entrar.
    const handlePatientWaiting = (data) => {
      if (onVideoCallScreen()) return;
      if (!localStorage.getItem("token")) return;
      setWaiting(data);
    };

    const clearCall = () => setCallData(null);

    socket.on("callUser", handleCall);
    socket.on("callEnded", clearCall);
    socket.on("callCancelled", clearCall);
    socket.on("patientWaiting", handlePatientWaiting);

    return () => {
      socket.off("me", authenticate);
      socket.off("callUser", handleCall);
      socket.off("callEnded", clearCall);
      socket.off("callCancelled", clearCall);
      socket.off("patientWaiting", handlePatientWaiting);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnswer = () => {
    if (!callData) return;
    history.push(pathVideoCall, { pendingAnswer: callData });
    setCallData(null);
  };

  const handleReject = () => {
    if (callData?.from) socket.emit("rejectCall", { to: callData.from });
    setCallData(null);
  };

  // Entrar en la sala llamando al paciente que espera.
  const handleJoinWaiting = () => {
    if (!waiting) return;
    history.push(pathVideoCall, {
      patient: { id: waiting.patientId, label: waiting.patientCode },
      sessionId: waiting.sessionId,
    });
    setWaiting(null);
  };

  if (callData) {
    return (
      <Popup>
        <CallerText>{t("call.incoming", { name: callData.name || t("call.unknownUser") })}</CallerText>
        <ActionRow>
          <AnswerBtn onClick={handleAnswer}>{t("call.answer")}</AnswerBtn>
          <RejectBtn onClick={handleReject}>{t("call.reject")}</RejectBtn>
        </ActionRow>
      </Popup>
    );
  }

  if (waiting) {
    return (
      <Popup>
        <CallerText>{t("call.waitingRoom", { code: waiting.patientCode })}</CallerText>
        <ActionRow>
          <AnswerBtn onClick={handleJoinWaiting}>{t("call.joinRoom")}</AnswerBtn>
          <RejectBtn onClick={() => setWaiting(null)}>{t("common.cancel")}</RejectBtn>
        </ActionRow>
      </Popup>
    );
  }

  return null;
};

export default CallNotification;
